import { PRIORITY_ORDER, type Priority, type ProjectTaskGroup, type Task } from '../../services/taskList';
import type { AmpelFarbe } from './planungAmpel';
import { type AmpelAuszug, fristenHinweis, hauptgrund, weiterFuer, weiterKurz } from './planungKachel';

/**
 * Die Planungs-Ampel in der Aufgabenliste (Stufe 1.5, Entscheid 20.09.:
 * `planning_general` steht schon in TASK_DOCUMENT_ASSIGNMENTS — die Konstante
 * bleibt unangetastet). Hier wird nur die VORHANDENE Aufgabe einer
 * Planungsposition schaerfer: rot macht sie dringend, gelb mindestens „bald
 * faellig", und der Grund der Ampel ersetzt den generischen Text „Projekt
 * laeuft seit X Tagen". Neue Aufgaben entstehen hier nicht — ein Projekt ohne
 * Planungsposition steht in der Liste „Planungsphase — Handlungsbedarf".
 *
 * Ein technischer Lesefehler (`fehler`) stuft nichts hoch: er ist kein Befund.
 * Dashboard und Aufgabenliste rufen dieselbe Funktion, damit Kachel und
 * Liste dieselben Zahlen zeigen.
 */

const PRIO_RANG_NACH_FARBE: Partial<Record<AmpelFarbe, Priority>> = { rot: 'urgent', gelb: 'soon' };

/** Nur hochstufen, nie herabstufen. */
export const prioritaetMitAmpel = (bisher: Priority, farbe: AmpelFarbe): Priority => {
    const ziel = PRIO_RANG_NACH_FARBE[farbe];
    if (!ziel) return bisher;
    return PRIORITY_ORDER[ziel] < PRIORITY_ORDER[bisher] ? ziel : bisher;
};

/**
 * „Planungsphase: <Grund> · Weiter: <Station> · <Stand>" (Stufe L3) — der
 * Grund bleibt vorn, die naechste Handlung des Leitfadens kommt dahinter;
 * wiederholt ihr Stand nur den Grund, steht nur die Station (`weiterKurz`).
 * Ohne Weiter-Text wie bisher „<Grund> · 3 abgelaufene Fristen — im Modul
 * feststellen". Beides zugleich nie: sind Fristen abgelaufen, sagt der
 * Weiter-Text das schon (dieselbe Regel wie `listenHinweisZeilen`).
 * Leer ohne Grund und ohne Weiter-Text.
 */
export const planungAufgabenDetail = (ampel: AmpelAuszug, weiter: string = ''): string => {
    const grund = hauptgrund(ampel);
    const danach = weiterKurz(weiter, grund) || fristenHinweis(ampel.abgelaufeneFristen);
    const teile = [grund, danach].filter(Boolean);
    return teile.length ? `Planungsphase: ${teile.join(' · ')}` : '';
};

const KEIN_WEITER: Readonly<Record<string, string>> = {};

const sortiereAufgaben = (tasks: Task[]): Task[] =>
    [...tasks].sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]);

/**
 * `weiter` je Projektkennung (`fetchPlanungsAmpeln`); fehlt er, bleibt der
 * Detailtext wie vor Stufe L3.
 */
export const ergaenzePlanungAufgaben = (
    groups: ProjectTaskGroup[],
    ampeln: Readonly<Record<string, AmpelAuszug>>,
    weiter: Readonly<Record<string, string>> = KEIN_WEITER,
): ProjectTaskGroup[] => {
    let geaendert = false;
    const out = groups.map(g => {
        const id = g.project?.id as string;
        const ampel = ampeln[id];
        if (!ampel || (ampel.farbe !== 'rot' && ampel.farbe !== 'gelb')) return g;
        let getroffen = false;
        const tasks = g.tasks.map(t => {
            if (t.assignment !== 'planning_general') return t;
            getroffen = true;
            const detail = planungAufgabenDetail(ampel, weiterFuer(weiter, id));
            return { ...t, priority: prioritaetMitAmpel(t.priority, ampel.farbe), detail: detail || t.detail };
        });
        if (!getroffen) return g;
        geaendert = true;
        const sortiert = sortiereAufgaben(tasks);
        return {
            ...g,
            tasks: sortiert,
            urgentCount: sortiert.filter(t => t.priority === 'urgent').length,
            soonCount: sortiert.filter(t => t.priority === 'soon').length,
        };
    });
    // Unveraendert → dieselbe Referenz, damit Memos der Aufrufer stehen bleiben.
    if (!geaendert) return groups;
    // Dieselbe Reihenfolge wie buildTaskGroups: dringendstes Projekt zuerst.
    return out.sort((a, b) => {
        if (a.urgentCount !== b.urgentCount) return b.urgentCount - a.urgentCount;
        if (a.soonCount !== b.soonCount) return b.soonCount - a.soonCount;
        return a.projectName.localeCompare(b.projectName);
    });
};
